import type {
  FlowEntry,
  FlowMemberData,
} from '@/components/Approval/ApprovalCreate/ApprovalLineSection';
import type { ApprovalTypeReferrer } from '@/types/approval';

/**
 * 어드민이 양식에 고정해 둔 협조결재자/참조인 공통 헬퍼.
 *
 * 고정 여부 판별 기준은 양식 메타(fixedCoApprovers / fixedReferrers)에 들어있는
 * passportId 뿐이다. 작성 화면 응답에 별도 '고정' 플래그가 없어도 id 대조만으로
 * 판별되므로 협조자/참조인 양쪽이 같은 규칙을 쓰도록 여기 한 곳에 모아둔다.
 *
 * BE 가 빈 객체(`[{}]`)를 내려보낼 수 있어 id/name 이 모두 있는 항목만 유효 취급.
 */
export const getValidFixedMembers = (
  values: ApprovalTypeReferrer[],
): ApprovalTypeReferrer[] => values.filter((v) => v.id && v.name);

/** 고정 항목 id 집합 — 삭제 버튼 노출/초기화 대상 판정에 사용 */
export const toFixedMemberIds = (values: ApprovalTypeReferrer[]): Set<string> =>
  new Set(getValidFixedMembers(values).map((v) => v.id));

/** 이미 목록에 들어있는 id 를 제외한 고정 항목 — 보정(ensure)을 멱등하게 만든다 */
export const pickMissingFixedMembers = (
  values: ApprovalTypeReferrer[],
  existingIds: Set<string>,
): ApprovalTypeReferrer[] =>
  getValidFixedMembers(values).filter((v) => !existingIds.has(v.id));

/**
 * 양식 고정 결재자를 결재라인 맨 끝(협조 블록 뒤 포함)에 fixed 순서대로 한 번씩만 둔다.
 * 끝이 아닌 자리·협조 블록 안에 있으면 끝으로 옮기고, `addMissing` 이면 없는 사람을 채운다.
 * 이미 정렬돼 있으면 같은 참조를 돌려줘야 flow deps 로 도는 보정이 멈춘다.
 */
export const pinFixedApproversToEnd = (
  entries: FlowEntry[],
  fixed: ApprovalTypeReferrer[],
  addMissing: boolean,
): FlowEntry[] => {
  const fixedIds = new Set(fixed.map((r) => r.id));
  const isFixedMember = (m: FlowMemberData) =>
    !!m.userId && fixedIds.has(m.userId);

  const found = new Map<string, FlowMemberData>();
  // 원래 비어 있던 협조 블록은 드래그 복귀 지점일 수 있어 남긴다 — 고정 결재자를 빼서 빈 블록만 걷는다
  const rest = entries.flatMap<FlowEntry>((entry) => {
    if (entry.type === 'member') {
      if (!isFixedMember(entry.data)) return [entry];
      found.set(entry.data.userId as string, entry.data);
      return [];
    }
    if (!entry.members.some(isFixedMember)) return [entry];
    entry.members.filter(isFixedMember).forEach((m) => {
      found.set(m.userId as string, m);
    });
    const members = entry.members.filter((m) => !isFixedMember(m));
    return members.length > 0 ? [{ ...entry, members }] : [];
  });

  const tail: FlowEntry[] = fixed.flatMap<FlowEntry>((r) => {
    const existing = found.get(r.id);
    if (existing) return [{ type: 'member', data: existing }];
    if (!addMissing) return [];
    return [
      {
        type: 'member',
        data: {
          id: `apv-fixed-${r.id}`,
          userId: r.id,
          kind: 'approver',
          name: r.name,
          positionName: r.positionName,
          divisionName: r.divisionName,
          departmentName: r.departmentName,
        },
      },
    ];
  });

  const next = [...rest, ...tail];
  const unchanged =
    next.length === entries.length &&
    next.every((e, i) => {
      const prev = entries[i];
      if (e.type === 'member' && prev.type === 'member') {
        return e.data === prev.data;
      }
      return e === prev;
    });
  return unchanged ? entries : next;
};
